import {
  Alert,
  Anchor,
  Box,
  Button,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconLink, IconShare, IconCoin, IconShieldCheck } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthIdentityChooser } from "../../components/AuthIdentityChooser";
import { CitySelect } from "../../components/CitySelect";
import { OnboardingSection, OnboardingShell } from "../../components/OnboardingShell";
import { PhoneInput } from "../../components/PhoneInput";
import { TermsAcceptancePanel } from "../../components/TermsAcceptancePanel";
import { isTermsComplete } from "../../lib/termsAcceptance";
import { affiliateTerms, TERMS_VERSION } from "../../constants/platformTerms";
import { useAuth } from "../../context/useAuth";
import { authApi } from "../../lib/api";
import { ApiError } from "../../lib/apiClient";
import { AUTH_IDENTITY_COPY, JOIN_AFFILIATE_COPY } from "../../lib/kyc";
import { validateIndianPhoneField, toPhoneForApi } from "../../lib/phone";
import {
  REGISTRATION_PASSWORD_COPY,
  validatePasswordConfirmation,
} from "../../constants/account";
import "../../components/authAnimations.css";

export function JoinAffiliatePage() {
  const { applyTokenUser } = useAuth();
  const navigate = useNavigate();
  const [masterAccepted, setMasterAccepted] = useState(false);
  const [emailFormOpen, setEmailFormOpen] = useState(false);
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [termsVersion, setTermsVersion] = useState(TERMS_VERSION);

  const emailForm = useForm({
    initialValues: {
      full_name: "",
      email: "",
      phone: "",
      password: "",
      confirm_password: "",
      city: "",
    },
    validate: {
      full_name: (v) => (v.trim().length >= 2 ? null : "Enter your full name"),
      email: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : "Enter a valid email"),
      phone: validateIndianPhoneField,
      password: (v) => (v.length >= 8 ? null : "Password must be at least 8 characters"),
      confirm_password: (v, values) => validatePasswordConfirmation(values.password, v),
    },
  });

  useEffect(() => {
    authApi.joinTerms("affiliate").then((res) => setTermsVersion(res.version)).catch(() => {});
  }, []);

  const termsOk = isTermsComplete(affiliateTerms, masterAccepted, true);

  async function submitEmail() {
    const validation = emailForm.validate();
    if (validation.hasErrors || !termsOk) return;
    setEmailSubmitting(true);
    try {
      const res = await authApi.registerAffiliate({
        full_name: emailForm.values.full_name.trim(),
        email: emailForm.values.email.trim(),
        phone: toPhoneForApi(emailForm.values.phone),
        password: emailForm.values.password,
        city: emailForm.values.city.trim() || undefined,
        terms_version: termsVersion,
        terms_accepted: true,
      });
      applyTokenUser(res.access_token, res.refresh_token, res.user);
      notifications.show({ color: "green", title: "Welcome", message: JOIN_AFFILIATE_COPY.welcome });
      navigate("/kyc");
    } catch (err) {
      notifications.show({
        color: "red",
        title: "Could not register",
        message: err instanceof ApiError ? err.message : "Something went wrong",
      });
    } finally {
      setEmailSubmitting(false);
    }
  }

  return (
    <OnboardingShell
      role="affiliate"
      accent="violet"
      activeStep={1}
      heroTitle={
        <>
          Refer talent.
          <br />
          <span className="gradientText">Earn sourcing fees.</span>
        </>
      }
      heroSubtitle={JOIN_AFFILIATE_COPY.heroSubtitle}
      highlights={[
        { icon: IconShare, text: "One share link for your whole network" },
        { icon: IconLink, text: "Candidates join the platform talent pool" },
        { icon: IconCoin, text: "Sourcing fee on confirmed placements" },
      ]}
      footer={
        <>
          Already have an account?{" "}
          <Anchor component={Link} to="/login" fw="var(--axio-font-weight-semibold)" c="violet.7">
            Log in
          </Anchor>
        </>
      }
    >
      <Stack gap="lg">
        <div className="staggerEnter" style={{ animationDelay: "var(--axio-duration-quick)" }}>
          <Title order={3} fw="var(--axio-font-weight-extra-bold)" style={{ fontFamily: "var(--axio-font-head)" }}>
            Join as an affiliate
          </Title>
          <Text size="sm" c="dimmed" mt="var(--axio-unit-6)">
            {JOIN_AFFILIATE_COPY.intro}
          </Text>
        </div>

        <Box className="staggerEnter" style={{ animationDelay: "var(--axio-duration-standard)" }}>
          <OnboardingSection
            title={AUTH_IDENTITY_COPY.sectionTitle}
            description={AUTH_IDENTITY_COPY.emailOnlyDescription}
            accent="violet"
          >
            <AuthIdentityChooser
              emailOpen={emailFormOpen}
              onChooseEmail={() => setEmailFormOpen(true)}
              emailFields={
                <Stack gap="md">
                  <Box className="glowInput">
                    <TextInput label="Full name" size="md" withAsterisk {...emailForm.getInputProps("full_name")} styles={{ input: { transition: "var(--axio-transition-input)" } }} />
                  </Box>
                  <Box className="glowInput">
                    <TextInput label="Email" size="md" withAsterisk type="email" {...emailForm.getInputProps("email")} styles={{ input: { transition: "var(--axio-transition-input)" } }} />
                  </Box>
                  <Box className="glowInput">
                    <PhoneInput
                      label="Phone"
                      size="md"
                      required
                      value={emailForm.values.phone}
                      onChange={(value) => emailForm.setFieldValue("phone", value)}
                      error={emailForm.errors.phone}
                    />
                  </Box>
                  <Box className="glowInput">
                    <PasswordInput
                      label={REGISTRATION_PASSWORD_COPY.passwordLabel}
                      size="md"
                      withAsterisk
                      {...emailForm.getInputProps("password")}
                      styles={{
                        input: { transition: "var(--axio-transition-input)" },
                        visibilityToggle: { display: "none" },
                      }}
                    />
                  </Box>
                  <Box className="glowInput">
                    <PasswordInput
                      label={REGISTRATION_PASSWORD_COPY.confirmPasswordLabel}
                      size="md"
                      withAsterisk
                      {...emailForm.getInputProps("confirm_password")}
                      styles={{ input: { transition: "var(--axio-transition-input)" } }}
                    />
                  </Box>
                  <Box className="glowInput">
                    <CitySelect
                      label="City"
                      size="md"
                      publicCatalog
                      value={emailForm.values.city}
                      onChange={(city) => emailForm.setFieldValue("city", city)}
                    />
                  </Box>
                </Stack>
              }
            />
          </OnboardingSection>
        </Box>

        <Box className="staggerEnter" style={{ animationDelay: "var(--axio-duration-medium)" }}>
          <OnboardingSection
            title="Platform terms"
            description="Read and accept the affiliate terms to continue."
            accent="violet"
          >
            <TermsAcceptancePanel
              terms={affiliateTerms}
              masterAccepted={masterAccepted}
              onMasterAcceptedChange={setMasterAccepted}
              version={termsVersion}
              accent="violet"
            />
          </OnboardingSection>
        </Box>

        {!termsOk && emailFormOpen && (
          <Box className="staggerEnter" style={{ animationDelay: "var(--axio-duration-stagger5)" }}>
            <Alert color="yellow" variant="light" icon={<IconShieldCheck size="var(--axio-unit-18)" />}>
              Accept the terms above before creating your account.
            </Alert>
          </Box>
        )}

        {emailFormOpen && (
          <Box className="staggerEnter" style={{ animationDelay: "var(--axio-duration-deliberate)" }}>
            <Button
              className="ctaGlow"
              color="violet"
              size="md"
              fullWidth
              loading={emailSubmitting}
              disabled={!termsOk}
              onClick={submitEmail}
              style={{ height: "var(--axio-unit-48)", fontSize: "var(--axio-font-size-control)", fontWeight: "var(--axio-font-weight-semibold)" }}
            >
              Create affiliate account
            </Button>
          </Box>
        )}
      </Stack>
    </OnboardingShell>
  );
}
